import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';
import Manipulator from '../../utils/dom/manipulator';
import BaseComponent from '../BaseComponent';
import {
	CARD_FIELDSET_SELECTOR,
	CARD_FIELDS_INITIALISED_EVENT,
	POSTCODE_STATE_CHANGE_EVENT
} from '../PaymentCardFields';
import { getPaymentGatewayInstance } from './payment-provider-gateway';
import { takeCardPayment } from './clubspark-api';
import { handleConfirmPaymentResponse } from './imin-api';
import { PLAYERS_SELECT_SELECTOR } from '../CheckoutAddressDetails';
import { ErrorCode } from './errorUtils';
import { ErrorCode as LtaApiErrorCode } from './place-order-api';
import { getPaymentRequest, setupRadioInputs, setupWallet, paymentSuccessHandler, paymentErrorHandler } from './utils';
import Request from '../../utils/request';
import ConfirmationModal from '../ConfirmationModalComponent';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';

const DATA_KEY = 'lta.payment';
const PaymentErrorCode = {
	...ErrorCode,
	...LtaApiErrorCode
};
class PaymentMethodSelector extends BaseComponent {
	_payload = {}
	_paymentInProgress = false;
	
	get payload() {
		return this._payload;
	}
	
	set payload (_payload) {
		this._payload = _payload;
	}

	get paymentInProgress() {
		return window._paymentInProgress;
	}

	set paymentInProgress (paymentInProgress) {
		const placeOrderButton = SelectorEngine.findOne('#payment-btn');
		window._paymentInProgress = paymentInProgress;
		if(paymentInProgress ) {
			placeOrderButton.disabled = true;			
		} else {
			placeOrderButton.disabled = false;
		}
	}

	get securePaymentInProgress() {
		return window._3DSecurePaymentInProgress;
	}

	set securePaymentInProgress (securePaymentInProgress) {
		if(!securePaymentInProgress) {
			this.paymentInProgress = false;
			window._3DSecurePaymentInProgress = false;
			clearTimeout(this.paymentTimer);
		} else {
			const timeoutMessage = Manipulator.getDataAttribute(this._element, 'timeout-message') || 'Session timeout. Please try again.';
			const timeoutUrl = Manipulator.getDataAttribute(this._element, 'timeout-url');
			const timeoutMilliseconds = Manipulator.getDataAttribute(this._element, 'timeout-milliseconds');
			const cancelOrderUrl = Manipulator.getDataAttribute(this._element, 'cancel-order-url');
			const productID = Manipulator.getDataAttribute(this._element, 'product-id');
			if(timeoutMilliseconds) {
				clearTimeout(this.paymentTimer);
				this.paymentTimer = setTimeout(() => {
					this.paymentInProgress = false;
					if(cancelOrderUrl && productID) {
						Request.post(`${cancelOrderUrl}?productID=${productID}&reason=${timeoutMessage}`).finally(() => {							
							if(timeoutUrl) {
								clearTimeout(this.paymentTimer);
								window._3DSecurePaymentInProgress = false;
								window._paymentInProgress = false;
								window.location.href = timeoutUrl;
							}
						});
					}
					alert(timeoutMessage);
					clearTimeout(this.paymentTimer);
					window._3DSecurePaymentInProgress = false;
					this.paymentInProgress = false;
				 }, timeoutMilliseconds);
			} else {
				clearTimeout(this.paymentTimer);
			}
			this.paymentInProgress = securePaymentInProgress;
			window._3DSecurePaymentInProgress = securePaymentInProgress;
		}
	}

	static get NAME() {
		return DATA_KEY;
	}

	_onErrorCallback = [];

	onCheckoutError(callback){
		this._onErrorCallback.push(callback)
	}

	checkoutErrorHandler(error) {
		for(let callback of this._onErrorCallback) {
			callback(error);
		}
	}

	constructor(element) {
		super(element);

		this.provider = Manipulator.getDataAttribute(this._element, 'provider') || 'clubspark';
		this.paymentGateway = getPaymentGatewayInstance(this.provider, );
		// NOTE - Another Instance of Payment Gateway for 3DSecure Authentication. 
		// Clubspark and imin doesn't require this. This is only for Play
		this.paymentGateway_LTA = window.lta[this.provider].checkout.paymentGatewayKey_LTA || '';
		if(this.paymentGateway_LTA) {
			this.paymentGateway3DSecureInstance = getPaymentGatewayInstance(this.provider, this.paymentGateway_LTA);
		}

		this.cardElements = {};
		this.cardPostCodeState = {};

		this.cardFieldset = SelectorEngine.findOne(`#${CARD_FIELDSET_SELECTOR}`);

		const placeOrderButton = SelectorEngine.findOne('#payment-btn');
		const { totalCost } = window?.lta?.[this.provider]?.basket || { totalCost: null };
		// const paymentidentifier = window.lta[this.provider].checkout.paymentidentifier;
		// const source = window.lta.checkout.paymentSource;

		window.addEventListener('beforeunload', (event) => {
			if(window._3DSecurePaymentInProgress) {
				event.preventDefault();
				event.returnValue = false;
			}
		});

		window.addEventListener("unload", (event) => {
			if(window._3DSecurePaymentInProgress) {
				const cancelOrderUrl = Manipulator.getDataAttribute(this._element, 'cancel-order-url');
				const cancelOrderReason = Manipulator.getDataAttribute(this._element, 'user-left-reason');
				const productID = Manipulator.getDataAttribute(this._element, 'product-id');
				const reason = cancelOrderReason || 'User left the page';
				if(cancelOrderUrl && productID) {
					console.log("cancelOrderUrl", cancelOrderUrl);
					console.log("productID", productID);
					console.log("reason", reason);
					navigator.sendBeacon(`${cancelOrderUrl}?productID=${productID}&reason=${reason}`);
				}
			}
		});

		if (this.paymentGateway) {
			const paymentRequest = getPaymentRequest({ paymentGateway: this.paymentGateway, provider: this.provider });
			const walletSubmitButtonSelector = '#payment-request-button';
			const walletSubmitButton = SelectorEngine.findOne(walletSubmitButtonSelector);
			const cardFieldsetWrapper = this.cardFieldset?.parentElement;

			setupRadioInputs({
				cardFieldsetWrapper,
				paymentGateway: this.paymentGateway,
				paymentRequest,
				placeOrderButton,
				radioInputs: SelectorEngine.find('.lta-form-check-input[type=radio]', this._element),
				walletSubmitButton,
				getExtraParam: () => {
					this.extraParam = this.validateOnCheckout();
					return this.extraParam;
				}
			});

			setupWallet({
				paymentGateway: this.paymentGateway,
				paymentRequest,
				totalCost,
				walletSubmitButton,
				walletSubmitButtonSelector,
				provider: this.provider
			});

			EventHandler.on(
				this.cardFieldset,
				CARD_FIELDS_INITIALISED_EVENT,
				({ cardNumber, cardExpiry, cardCvc, postCodeInput }) => {
					this.cardElements = { cardNumber, cardExpiry, cardCvc, postCodeInput };
				}
			);

			EventHandler.on(this.cardFieldset, POSTCODE_STATE_CHANGE_EVENT, ({ value, isTouched, isInvalid }) => {
				this.cardPostCodeState = { value, isTouched, isInvalid };
			});
		}
		const consent = document.querySelector('.js-consent-selection');
		let consentValue = [];
		if(consent) {
			consent.addEventListener('change', (event) => {
				if (event.target.type === 'checkbox') {
					const checked = document.querySelectorAll('input[type="checkbox"]:checked');
					consentValue = [...checked].map((x) => x.value);
				}
			});
		}

		EventHandler.on(placeOrderButton, 'click', async (event) => {
			if(this.paymentInProgress) {
				return;
			}
			this.paymentInProgress = true;
			event.target.disabled = true;
			event.preventDefault();
			const consentVal = consentValue[0] || false;
			const numberOfPlayers = SelectorEngine.findOne(PLAYERS_SELECT_SELECTOR)?.value;
			this.extraParam = this.validateOnCheckout();
			this.extraParam = {
				...this.extraParam,
				...this.payload
			}
			if (!this.extraParam) return;
			if (this.extraParam.totalCost) {
				window.lta[this.provider].basket.totalCost = this.extraParam.totalCost;
			}
			if (this.abto !== undefined && this.abto === true) {
				const abtoCheckBox = SelectorEngine.findOne('#abtoCheck')
				const titleValue = Manipulator.getDataAttribute(abtoCheckBox, 'title');
				const descriptionValue = Manipulator.getDataAttribute(abtoCheckBox, 'description');

				const response = await this.getConfirmationAsync(titleValue,descriptionValue);
				if (!response.abto) {
					event.target.disabled = false;
					return;
				}
				this.extraParam.abto = this.abto;
			}
			if (this.isSaveCard !== undefined) {
				this.extraParam.isSaveCard = this.isSaveCard;
			}
			if (this.stripeCustomerRef !== undefined) {
				this.extraParam.stripeCustomerRef = this.stripeCustomerRef;
			}
			if (window?.lta?.checkout?.extraParams?.courseEventType) {
				this.extraParam.courseEventType = window.lta.checkout.extraParams.courseEventType;
			}
			// NB: Deliberately using == in case integration changes cause the value to be passed as a string
			if (this.provider === 'imin') {
				if (totalCost === 0) {
					sessionStorage.setItem('bookingToken', window.lta[this.provider].basket.iminbookingToken);
					paymentSuccessHandler({
						id: null,
						extraParam: { ...this.extraParam, source: this.provider, numberOfPlayers },
						provider: this.provider
					});
				} else {
					sessionStorage.setItem('IminplayerCount', numberOfPlayers);
					sessionStorage.setItem('bookingToken', window.lta[this.provider].basket.iminbookingToken);
					sessionStorage.setItem('paymentidentifier', window.lta[this.provider].checkout.paymentidentifier);
					sessionStorage.setItem('source', this.provider);

					const { error } = await handleConfirmPaymentResponse();

					if (error) {
						sessionStorage.removeItem('paymentidentifier');
						sessionStorage.removeItem('IminplayerCount');
						sessionStorage.removeItem('bookingToken');
						sessionStorage.removeItem('source');

						paymentErrorHandler(
							{ message: 'ERROR WITH THE PAYMENT METHOD', isSubmissionError: true },
							PaymentErrorCode.PlaceOrderApiError
						);

						// eslint-disable-next-line consistent-return
						return error;
					}
					this.paymentInProgress = false;
				}
			} else if (totalCost === 0) {
				paymentSuccessHandler({
					id: undefined,
					extraParam: { ...this.extraParam, playParkConsent: consentVal },
					provider: this.provider,
					checkoutErrorHandler: this.checkoutErrorHandler.bind(this)
				});
			} else {
				startLoadingAnimation();
				EventHandler.trigger(this.cardElements.postCodeInput, 'change');

				if (this.extraParam?.paymentId) {
					paymentSuccessHandler({
						// eslint-disable-next-line unicorn/no-null
						id: this.extraParam?.paymentID,
						extraParam: { ...this.extraParam, playParkConsent: consentVal },
						checkoutErrorHandler: this.checkoutErrorHandler.bind(this)
					});
				} else {
					// prettier-ignore
					takeCardPayment(
						this.paymentGateway,
						this.cardElements.cardNumber,
						this.cardPostCodeState,
						this.paymentGateway3DSecureInstance,
						paymentSuccessHandler,
						paymentErrorHandler,
						{...this.extraParam, isCardPayment: true, playParkConsent: consentVal},
						() => {
							this.paymentInProgress = true;
						},
						() => {
							this.paymentInProgress = false;
						},
						() => {
							this.securePaymentInProgress = true;
						},
						() => {
							this.securePaymentInProgress = false;
						}
					);
				}
			}
		});
	}

	async cardPaymentAsync(params, successHandler, errorHandler) {
		try {
			const methodParameters = {
					type: 'card',
					card: params?.cardElements?.cardNumber,
					billing_details: {
						name: params?.name,
						email: params?.email,
						address: {
							postal_code: params?.postalCode?.value
						}
					}
				};
				startLoadingAnimation(true);
				const paymentMethodResult = await this.paymentGateway.createPaymentMethod(methodParameters);
				stopLoadingAnimation();
				if (paymentMethodResult.error || this.cardPostCodeState.isInvalid) {
					errorHandler(paymentMethodResult.error);
					return false;
				} else {
					startLoadingAnimation(true);
					let response = await successHandler(paymentMethodResult.paymentMethod.id, false);
					stopLoadingAnimation();
					if(response.Status === "REQ_AUTHORIZATION") {
						// 3D Secure Flow here
						if(response.ClientSecret) {
							this.securePaymentInProgress = true;			
							const { paymentIntent, error } = await this.paymentGateway3DSecureInstance.confirmCardPayment(response.ClientSecret)
							if(error) {
								startLoadingAnimation(true);
								const cancelPayment = await this.captureCardPayment(error?.payment_intent?.id);
								errorHandler({ message: cancelPayment?.ErrorMessage || "Something went wrong!" });
								stopLoadingAnimation();
								this.securePaymentInProgress = false;
								return false;
							} else {
								startLoadingAnimation(true);
								const capturePayment = await this.captureCardPayment(paymentIntent?.id);
								stopLoadingAnimation();
								if (capturePayment.Success || capturePayment.success) {
									startLoadingAnimation(true);
									response = await successHandler(paymentIntent?.id, true);
									stopLoadingAnimation();
									if(!response.Success && !response.success) {
										errorHandler({ message: response?.ErrorMessage || "Something went wrong!" });
										this.securePaymentInProgress = false;
										return false;
									}
								} else {
									errorHandler({ message: capturePayment?.ErrorMessage || "Something went wrong!" });
									this.securePaymentInProgress = false;
									return false;
								}
							}
							this.securePaymentInProgress = false;
						} else {
							errorHandler({ message: response?.ErrorMessage || "Something went wrong!" });
							this.securePaymentInProgress = false;
							return false;
						}
					}
					if(!response.Success && !response.success) {
						errorHandler({ message: response?.ErrorMessage || "Something went wrong!" });
					}
					return true;
				}
			} catch(error) {
				stopLoadingAnimation();
				console.error(error);
				errorHandler(error);
			}
			this.paymentInProgress = false;
	}

	captureCardPayment(paymentIntentId) {
		return Request.post('/api/payments/capture', {
			PaymentIntentId: paymentIntentId
		});
	}

	getConfirmationAsync = (title, description) => {
		return new Promise((resolve, reject) => {
			const confirmationModal = new ConfirmationModal(SelectorEngine.findOne('.p-checkout'), {
				data: {
					id: 'js-remove-venue-confirmation-checkout',
					show: false,
					title: title || 'Title',
					description: description || 'Description',
					actionButtonId: 1,
					primaryButtonLabel: 'Proceed',
					secondaryButtonLabel: 'Go Back',
					primaryButtonClick: (event) => {
						resolve({ abto: true });
					},
					secondaryButtonClick: () => {
						resolve({ abto: false });
					}
				}
			});
			confirmationModal.toggleModal(true);
		});
	};

	validateOnCheckout() {
		return {};
	}
}

export default PaymentMethodSelector;
